import { apiFetch } from "@/lib/apiClient";
import { SessionExpiredError } from "@/lib/authApi";

export interface CashEntry {
  id: number;
  date: string;
  store: number;
  store_name?: string;
  user_id: string;
  user_name: string;
  user_role: string;
  start_amount: number | null;
  end_amount: number | null;
  calculated_end_amount: number | null;
  notes: string | null;
}

const requestJson = async (url: string, options: RequestInit = {}) => {
  const response = await apiFetch(url, options);
  if (!response.ok) {
    if (response.status === 401) {
      throw new SessionExpiredError();
    }
    let errorMessage = "Échec de la requête API";
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (typeof errorData === "object") {
        // Handle Django REST Framework field errors.
        const firstKey = Object.keys(errorData)[0];
        if (firstKey) {
          const fieldError = errorData[firstKey];
          if (Array.isArray(fieldError)) {
            errorMessage = `${firstKey}: ${fieldError[0]}`;
          } else if (typeof fieldError === "string") {
            errorMessage = `${firstKey}: ${fieldError}`;
          }
        }
      }
    } catch {
      // Non-JSON errors keep the default message.
    }
    throw Object.assign(new Error(errorMessage), { status: response.status });
  }
  return response.json();
};

const createCashEntry = (entry: Partial<CashEntry>): Promise<CashEntry> => {
  return requestJson("/stats/cash-entries/", {
    method: "POST",
    body: JSON.stringify(entry),
  });
};

const updateCashEntry = (
  id: number,
  updates: Partial<CashEntry>,
): Promise<CashEntry> => {
  return requestJson(`/stats/cash-entries/${id}/`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
};

const getTodaysCashEntryForUser = (
  date: string,
  signal?: AbortSignal,
): Promise<CashEntry | null> => {
  // The backend filters by the logged-in user from the token.
  return requestJson(`/stats/cash-entries/?date=${date}`, { signal }).then((data) =>
    data.length > 0 ? data[0] : null,
  );
};

const getAllCashEntries = (): Promise<CashEntry[]> => {
  return requestJson("/stats/cash-entries/");
};

const getUnclosedCashEntriesForUser = (): Promise<CashEntry[]> => {
  // The backend filters by user; it has no endpoint for unclosed entries.
  return requestJson("/stats/cash-entries/").then((entries: CashEntry[]) =>
    entries.filter((entry) => entry.end_amount === null),
  );
};

// Keeps the hook shape of the other `use*Api` hooks; the module-level
// functions stay referentially stable for effect dependencies.
export const useCashEntryApi = () => {
  return {
    createCashEntry,
    updateCashEntry,
    getTodaysCashEntryForUser,
    getAllCashEntries,
    getUnclosedCashEntriesForUser,
  };
};
